import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { attachmentDownloadUrl, deleteAttachment, fetchAttachments } from '../api/client';

interface AttachmentListProps {
  kind: 'topic' | 'comment';
  id: string;
  /** The signed-in viewer's own id, so only the uploader sees a Delete button. */
  principalId: string | undefined;
}

/**
 * Renders nothing until there's at least one attachment — an empty state
 * here would just be noise on every topic/comment, most of which have none.
 */
export function AttachmentList({ kind, id, principalId }: AttachmentListProps): React.JSX.Element | null {
  const queryClient = useQueryClient();
  const query = useQuery({ queryKey: ['attachments', kind, id], queryFn: () => fetchAttachments(kind, id) });

  const remove = useMutation({
    mutationFn: (attachmentId: string) => deleteAttachment(attachmentId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['attachments', kind, id] }),
  });

  // A failed fetch must not look like "no attachments" — that hides a real
  // problem (e.g. Blob Storage unreachable) as if the post just has none.
  if (query.error) {
    return (
      <p role="alert" data-testid={`attachment-list-error-${kind}-${id}`}>
        Could not load attachments.
      </p>
    );
  }

  if (!query.data || query.data.length === 0) {
    return null;
  }

  return (
    <>
      {remove.error && (
        <p role="alert" className="notice warning" data-testid={`attachment-delete-error-${kind}-${id}`}>
          Could not delete that attachment. Try again.
        </p>
      )}
      <ul className="attachment-list" data-testid={`attachment-list-${kind}-${id}`}>
      {query.data.map((attachment) => (
        <li key={attachment.id} className="attachment-row" data-testid={`attachment-${attachment.id}`}>
          {attachment.scanState !== 'Clean' ? (
            <span className="badge" data-testid={`attachment-scanning-${attachment.id}`}>
              {attachment.fileName} · scanning for safety — not visible yet
            </span>
          ) : attachment.contentType.startsWith('image/') ? (
            <a href={attachmentDownloadUrl(attachment.id)} target="_blank" rel="noopener noreferrer">
              <img
                src={attachmentDownloadUrl(attachment.id)}
                alt={attachment.fileName}
                className="attachment-thumbnail"
              />
            </a>
          ) : (
            <a href={attachmentDownloadUrl(attachment.id)} target="_blank" rel="noopener noreferrer">
              {attachment.fileName}
            </a>
          )}

          {principalId === attachment.uploadedById && (
            <button
              type="button"
              className="button secondary small"
              disabled={remove.isPending}
              onClick={() => remove.mutate(attachment.id)}
              data-testid={`attachment-delete-${attachment.id}`}
            >
              Delete
            </button>
          )}
        </li>
      ))}
      </ul>
    </>
  );
}
